// ============================================================================
// PulseGraphic — the hero's custom animated illustration (REAL motion).
// ----------------------------------------------------------------------------
// A bespoke, brand-coded SVG built ENTIRELY from Pulse tokens (coral→orange,
// violet, ocean, lime gradients). It animates with Framer Motion:
//   • a live "pulse" ECG/strength waveform that draws itself on a loop,
//   • a sweeping progress ring that fills then resets,
//   • three orbiting metric nodes (one per pillar) that breathe + drift,
//   • a soft glow that swells in time with the wave.
// All motion is gated behind useReducedMotion: when a visitor prefers reduced
// motion the graphic renders as a clean, fully-drawn static composition. No
// raster images, no external assets — pure vector so it stays razor-sharp at
// every viewport. Decorative only (aria-hidden); the headline carries meaning.
// ============================================================================
import { memo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { Transition } from "framer-motion";

const EASE = [0.22, 0.61, 0.36, 1] as const;

// The strength/heartbeat waveform path — a flat baseline, a sharp PR spike,
// a recovery dip, then a steady climb. Reads as "effort → progress".
const WAVE =
  "M8 96 H64 l14 -52 18 92 14 -78 12 40 h26 l16 -26 14 14 H300";

function OrbitNode({
  cx,
  cy,
  r,
  gradId,
  delay,
  reduce,
  glyph,
}: {
  cx: number;
  cy: number;
  r: number;
  gradId: string;
  delay: number;
  reduce: boolean | null;
  glyph: "users" | "calendar" | "chart";
}) {
  const float: Transition = {
    duration: 4.5,
    delay,
    repeat: Infinity,
    repeatType: "mirror",
    ease: "easeInOut",
  };
  return (
    <motion.g
      initial={reduce ? false : { opacity: 0, scale: 0.6 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: delay + 0.2, ease: EASE }}
    >
      <motion.g
        animate={reduce ? undefined : { y: [0, -7, 0] }}
        transition={reduce ? undefined : float}
      >
        {/* pulsing halo */}
        {!reduce && (
          <motion.circle
            cx={cx}
            cy={cy}
            r={r}
            fill={`url(#${gradId})`}
            opacity={0.25}
            animate={{ r: [r, r + 9, r], opacity: [0.25, 0, 0.25] }}
            transition={{ duration: 2.6, delay, repeat: Infinity, ease: "easeOut" }}
          />
        )}
        <circle cx={cx} cy={cy} r={r} fill={`url(#${gradId})`} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#fff" strokeOpacity={0.5} strokeWidth={1.5} />
        <g stroke="#fff" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" fill="none" transform={`translate(${cx - 9} ${cy - 9})`}>
          {glyph === "users" && (
            <>
              <circle cx={6} cy={6} r={3} />
              <path d="M1.5 17a5 5 0 0 1 9 0" />
              <path d="M12 4.2a3 3 0 0 1 0 5.6M16.5 17a5 5 0 0 0-3-4.6" />
            </>
          )}
          {glyph === "calendar" && (
            <>
              <rect x={1.5} y={3} width={15} height={13.5} rx={2.5} />
              <path d="M1.5 7h15M6 1.5v3M12 1.5v3" />
            </>
          )}
          {glyph === "chart" && (
            <>
              <path d="M2 16V8M9 16V3M16 16v-6" />
              <path d="M1 17.5h16" />
            </>
          )}
        </g>
      </motion.g>
    </motion.g>
  );
}

export const PulseGraphic = memo(function PulseGraphic({
  className,
}: {
  className?: string;
}) {
  const reduce = useReducedMotion();

  return (
    <svg
      viewBox="0 0 340 320"
      className={className}
      role="img"
      aria-hidden
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="pg-coral" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FF5E62" />
          <stop offset="1" stopColor="#FF9966" />
        </linearGradient>
        <linearGradient id="pg-violet" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#6A5BFF" />
          <stop offset="1" stopColor="#C86BFF" />
        </linearGradient>
        <linearGradient id="pg-ocean" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2D8FFF" />
          <stop offset="1" stopColor="#5B6BFF" />
        </linearGradient>
        <linearGradient id="pg-lime" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#18C964" />
          <stop offset="1" stopColor="#3BE085" />
        </linearGradient>
        <linearGradient id="pg-wave" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FF5E62" />
          <stop offset="0.5" stopColor="#FF8A3D" />
          <stop offset="1" stopColor="#7B5BFF" />
        </linearGradient>
        <radialGradient id="pg-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#FF7A45" stopOpacity={0.55} />
          <stop offset="1" stopColor="#FF7A45" stopOpacity={0} />
        </radialGradient>
      </defs>

      {/* Ambient swelling glow behind the disc. */}
      <motion.circle
        cx={170}
        cy={158}
        r={140}
        fill="url(#pg-glow)"
        animate={reduce ? undefined : { opacity: [0.45, 0.8, 0.45], scale: [0.96, 1.04, 0.96] }}
        transition={reduce ? undefined : { duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "170px 158px" }}
      />

      {/* The disc / card the wave sits on. */}
      <motion.g
        initial={reduce ? false : { opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: EASE }}
      >
        <circle cx={170} cy={158} r={118} fill="#FFFFFF" />
        <circle cx={170} cy={158} r={118} fill="none" stroke="#EAEDF5" strokeWidth={1.5} />

        {/* Sweeping progress ring — fills to ~78%, holds, resets. Animated via
            strokeDashoffset over the full circumference (2·π·104 ≈ 653.45). */}
        <circle cx={170} cy={158} r={104} fill="none" stroke="#F1F3FA" strokeWidth={8} />
        <motion.circle
          cx={170}
          cy={158}
          r={104}
          fill="none"
          stroke="url(#pg-wave)"
          strokeWidth={8}
          strokeLinecap="round"
          transform="rotate(-90 170 158)"
          strokeDasharray={653.45}
          initial={reduce ? false : { strokeDashoffset: 653.45 }}
          animate={
            reduce
              ? { strokeDashoffset: 143.76 }
              : { strokeDashoffset: [653.45, 143.76, 143.76] }
          }
          transition={
            reduce
              ? undefined
              : {
                  duration: 3.2,
                  times: [0, 0.6, 1],
                  repeat: Infinity,
                  repeatDelay: 0.4,
                  ease: EASE,
                }
          }
        />
      </motion.g>

      {/* The live strength/heartbeat waveform, clipped to the disc. */}
      <g clipPath="none">
        <g transform="translate(20 110)">
          {/* faint baseline grid lines for "instrument" feel */}
          <g stroke="#EEF1F8" strokeWidth={1}>
            <line x1={8} y1={48} x2={300} y2={48} />
            <line x1={8} y1={96} x2={300} y2={96} />
          </g>
          {/* ghost full path so the line has a track */}
          <path d={WAVE} fill="none" stroke="#F1F3FA" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" />
          {/* the animated, self-drawing pulse line */}
          <motion.path
            d={WAVE}
            fill="none"
            stroke="url(#pg-wave)"
            strokeWidth={4.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={reduce ? false : { pathLength: 0, opacity: 0.2 }}
            animate={
              reduce
                ? { pathLength: 1, opacity: 1 }
                : { pathLength: [0, 1, 1], opacity: [0.4, 1, 1] }
            }
            transition={
              reduce
                ? undefined
                : { duration: 3.2, times: [0, 0.7, 1], repeat: Infinity, repeatDelay: 0.4, ease: EASE }
            }
          />
          {/* a bright tracer dot riding the spike */}
          {!reduce && (
            <motion.circle
              r={4.5}
              fill="#FF5E62"
              stroke="#fff"
              strokeWidth={2}
              animate={{
                cx: [8, 96, 124, 240, 300],
                cy: [96, 44, 18, 70, 70],
                opacity: [0, 1, 1, 1, 0],
              }}
              transition={{ duration: 3.2, repeat: Infinity, repeatDelay: 0.4, ease: EASE }}
            />
          )}
        </g>
      </g>

      {/* Orbiting metric nodes — clients / calendar / metrics. */}
      <OrbitNode cx={64} cy={70} r={26} gradId="pg-violet" delay={0.1} reduce={reduce} glyph="users" />
      <OrbitNode cx={286} cy={92} r={24} gradId="pg-ocean" delay={0.5} reduce={reduce} glyph="calendar" />
      <OrbitNode cx={258} cy={262} r={26} gradId="pg-lime" delay={0.9} reduce={reduce} glyph="chart" />

      {/* A small coral "live" pip in the corner of the disc. */}
      <motion.circle
        cx={92}
        cy={250}
        r={9}
        fill="url(#pg-coral)"
        animate={reduce ? undefined : { scale: [1, 1.18, 1] }}
        transition={reduce ? undefined : { duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "92px 250px" }}
      />
    </svg>
  );
});
